import { handle401Response } from "./authRedirect";

type AnyAsyncMethod = (...args: unknown[]) => Promise<unknown>;

/**
 * Wraps a BFF client method to handle 401 responses globally.
 * When a 401 is detected, triggers re-authentication via handle401Response().
 */
function wrapMethodWithErrorHandler<T extends AnyAsyncMethod>(originalMethod: T, methodName: string): T {
  return (async (...args: Parameters<T>) => {
    try {
      return await originalMethod(...args);
    } catch (error) {
      if (is401Error(error)) {
        // eslint-disable-next-line no-console
        console.warn(`[authInterceptor] 401 Unauthorized from ${methodName}`, {
          error: error instanceof Error ? error.message : String(error),
        });

        await handle401Response();
      }

      throw error;
    }
  }) as T;
}

/**
 * Checks if an error is a 401 Unauthorized response.
 * Handles multiple error formats (Axios, fetch, etc.).
 */
function is401Error(error: unknown): boolean {
  if (!error || typeof error !== "object") return false;

  const errorObj = error as {
    response?: { status?: number };
    status?: number | string;
    message?: string;
    request?: unknown;
  };

  if (errorObj.response?.status === 401) return true;

  if (Number.parseInt(String(errorObj.status), 10) === 401) return true;

  if (typeof errorObj.message === "string" && errorObj.request) {
    const msg = errorObj.message.toLowerCase();
    if (msg.includes("401") || msg.includes("unauthorized")) return true;
  }

  return false;
}

/**
 * Collects every function-valued property on a BFF client, whether it
 * sits on the prototype (class methods, the step6-bff-client v1.0.142
 * shape) or on the instance (arrow-function properties assigned in a
 * constructor, the shape some alternate openapi-generator templates
 * emit). Walking both keeps the wrapper robust to upstream codegen
 * changes — a future bump that switches templates won't silently leak
 * 401s past the interceptor.
 * @param {object} client - The BFF client instance to scan.
 * @returns {string[]} Names of function-valued properties on the client.
 */
function collectMethodNames(client: object): string[] {
  const names = new Set<string>();
  const prototype = Object.getPrototypeOf(client) as object | null;

  if (prototype) {
    for (const name of Object.getOwnPropertyNames(prototype)) {
      if (name === "constructor") continue;
      if (typeof (client as Record<string, unknown>)[name] === "function") {
        names.add(name);
      }
    }
  }

  for (const name of Object.getOwnPropertyNames(client)) {
    if (typeof (client as Record<string, unknown>)[name] === "function") {
      names.add(name);
    }
  }

  return [...names];
}

/**
 * Wraps a BFF client with authentication error handling.
 * Every async method — prototype or instance — is replaced with a
 * 401-aware shim that delegates to handle401Response() on
 * `Unauthorized` responses (Axios, fetch, or message-string shapes).
 * Non-function properties pass through untouched.
 * @param {T} client - The BFF client instance to wrap.
 * @returns {T} A new object that shares the client's prototype but exposes wrapped methods.
 */
export function wrapBFFClientWithAuth<T extends object>(client: T): T {
  const prototype = Object.getPrototypeOf(client) as object;
  const wrappedClient = Object.create(prototype) as T;

  Object.assign(wrappedClient, client);

  for (const methodName of collectMethodNames(client)) {
    const originalMethod = (client as Record<string, unknown>)[methodName] as AnyAsyncMethod;
    (wrappedClient as Record<string, unknown>)[methodName] = wrapMethodWithErrorHandler(
      originalMethod.bind(client),
      methodName
    );
  }

  return wrappedClient;
}

export { is401Error };
